import { validateField, validateForm, rerenderValidationNew } from "../utils/validation";
import { CONTACT_US_VALIDATION_RULES } from "../validation/contactUsValidationRules";
import { showToast } from "../_components/toast";
import { trans } from "../i18n/i18n";
import { Toast } from "../consts/toast";
import { trackEvent } from "../tracking/gtm";
import { getRecaptchaToken } from "../utils/recaptcha";

document.addEventListener("DOMContentLoaded", function (e) {
    initContactUsPage();
});

/**
 * Builds a validation whitelist for Contact Us fields.
 * Removes name/email when the user is logged in.
 *
 * @returns {Record<string, boolean>}
 */
function getContactUsWhitelist() {
    const base = Object.fromEntries(
        Object.keys(CONTACT_US_VALIDATION_RULES).map(key => [key, true])
    );

    const isLoggedIn = document.documentElement.classList.contains('is-logged-in');

    if (isLoggedIn) {
        delete base.first_name;
        delete base.last_name;
        delete base.email;
    }

    return base;
}

/**
 * Initializes Contact Us form behavior:
 * - renders server-side errors
 * - updates submit button state
 * - switches to success state if flagged
 * - binds input/change validation and submit handler
 *
 * @returns {void}
 */
function initContactUsPage() {
    const form = document.getElementById("contact-us-form");
    if (!form) return;

    showServerSideErrors(form);

    if (form.dataset?.contactSuccess === "true") {
        showContactUsSuccessState(form);
        return;
    }

    form.addEventListener("input", handleContactUsFormInput);
    form.addEventListener("change", handleContactUsFormInput);
    form.addEventListener("submit", submitContactUsForm);
}

/**
 * Validates a single field on input/change and updates UI + submit state.
 *
 * @param {Event} e
 * @returns {void}
 */
function handleContactUsFormInput(e) {
    const inputEl = e.target;
    const form = inputEl.closest("form");

    const allErrors = validateField(form, inputEl, CONTACT_US_VALIDATION_RULES, getContactUsWhitelist());
    const fieldErrors = allErrors?.[inputEl.name] || [];

    rerenderValidationNew(inputEl, fieldErrors);
    updateSubmitButtonState(form);
}

/**
 * Toggles the disabled state for all form controls.
 *
 * @param {HTMLFormElement} form
 * @param {boolean} isDisabled
 * @returns {void}
 */
function setFormDisabledState(form, isDisabled) {
    const elements = form.querySelectorAll("input, select, textarea, button");
    elements.forEach((el) => {
        el.disabled = isDisabled;
    });
}

/**
 * Handles Contact Us form submit:
 * - prevents native submit
 * - validates the full form client-side
 * - adds reCAPTCHA token (if configured)
 * - disables form and submits
 *
 * @param {Event} e
 * @returns {Promise<void>}
 */
async function submitContactUsForm(e) {
    preventFormSubmission(e);

    const form = e.target.closest ? e.target.closest("form") : e.target;
    if (!form) return;

    // Prevent double submit
    if (form.dataset.submitting === "1") {
        return;
    }
    form.dataset.submitting = "1";

    const spinner = document.getElementById("contactUsFormSpinner");
    const submitBtn = form.querySelector('button[type="submit"]');

    // Lock UI immediately
    submitBtn && (submitBtn.disabled = true);
    setFormDisabledState(form, true);
    spinner?.classList.remove("d-none");

    const errors = validateForm(form, CONTACT_US_VALIDATION_RULES, getContactUsWhitelist());
    if (errors) {
        showToast(trans("Please correct the errors in the form before submitting."), Toast.ERROR);

        // Unlock if validation failed
        spinner?.classList.add("d-none");
        setFormDisabledState(form, false);
        delete form.dataset.submitting;
        updateSubmitButtonState(form);
        return;
    }

    await appendRecaptchaToken(form);

    form.submit();
}


/**
 * Enables/disables the submit button based on whether all required fields
 * are filled (and meet basic minlength checks).
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function updateSubmitButtonState(form) {
    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) return;

    const errors = validateForm(form, CONTACT_US_VALIDATION_RULES, getContactUsWhitelist(), {renderErrors: false});
    submitBtn.disabled = !!errors;
}

/**
 * Applies server-side validation errors (returned after a redirect) to matching form fields.
 *
 * Expects `form.dataset.serverErrors` to be a JSON string in the shape:
 * `{ [fieldName: string]: string[] }`.
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function showServerSideErrors(form) {
    const errorsRaw = form.dataset?.serverErrors;
    if (!errorsRaw) return;

    try {
        /** @type {Record<string, string[]>} */
        const errors = JSON.parse(errorsRaw);

        Object.entries(errors).forEach(([name, messages]) => {
            const input = form.querySelector(`[name="${name}"]`);
            if (!input) return;

            rerenderValidationNew(input, messages);
        });

        updateSubmitButtonState(form);
    } catch (err) {
        console.error("Unable to parse server validation errors", err);
    }
}

/**
 * Cancels a form event and stops it from bubbling to other handlers.
 *
 * @param {Event} event
 * @returns {void}
 */
function preventFormSubmission(event) {
    if (typeof event.preventDefault === "function") event.preventDefault();
    if (typeof event.stopPropagation === "function") event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
}

/**
 * Fetches a reCAPTCHA v3 token for the given action and stores it into the hidden form field.
 *
 * @param {HTMLFormElement} form
 * @returns {Promise<void>}
 */
async function appendRecaptchaToken(form) {
    const recaptchaAction = form.getAttribute('data-recaptcha-action');
    const recaptchaField = form.querySelector('input[name="recaptcha_token"]');

    if (!recaptchaAction || !recaptchaField) {
        return;
    }

    try {
        recaptchaField.value = await getRecaptchaToken(recaptchaAction);
    } catch (_) {
        recaptchaField.value = "";
    }
}

/**
 * Switches the UI to the "thank you" state after a successful submit
 * and sends tracking events.
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function showContactUsSuccessState(form) {
    form.querySelector(".row.g-3")?.classList.add("d-none");
    document.getElementById("contact-us-form-thankyou")?.classList.remove("d-none");

    const userId = Number(form.dataset?.contactUserId || 0);
    trackEvent("vinEvent", {
        event_category: "ContactUs",
        event_action: "Sent",
        user_id: userId
    });
    trackEvent("contact_form");
}

